import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useUserData } from "context/OAuth/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  replaceLangInAuthPath,
  storeAuthLang,
  normalizeAuthLang,
} from "utils/authLanguage";

const useLanguageSelector = () => {
  const { i18n } = useTranslation();
  const { handleSetLanguage } = useUserData();
  const { lang: langFromUrl } = useParams();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const languages = [
    { code: "nl", label: "Dutch", flag: "🇳🇱" },
    { code: "en", label: "English", flag: "🇺🇸" },
  ];

  const syncLanguage = useCallback(
    (lng) => {
      const normalized = normalizeAuthLang(lng);
      storeAuthLang(normalized);
      handleSetLanguage(normalized);
    },
    [handleSetLanguage],
  );

  const updateUrlLanguage = useCallback(
    (lng) => {
      const { pathname, search, hash } = location;
      const newPath = replaceLangInAuthPath(pathname, lng);
      if (newPath !== pathname) {
        navigate(`${newPath}${search}${hash}`, { replace: true });
      }
    },
    [location, navigate],
  );

  // Keep i18n in sync when navigating between auth pages (e.g. login -> password-reset)
  useEffect(() => {
    if (langFromUrl) {
      syncLanguage(langFromUrl);
    }
  }, [langFromUrl, syncLanguage]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    }

    if (langDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [langDropdownOpen]);

  const onChangeLanguage = useCallback(
    (lng) => {
      syncLanguage(lng);
      setLangDropdownOpen(false);
      updateUrlLanguage(lng);
    },
    [syncLanguage, updateUrlLanguage],
  );

  return {
    langDropdownOpen,
    setLangDropdownOpen,
    langRef,
    languages,
    changeLanguage: onChangeLanguage,
    currentLanguage: normalizeAuthLang(i18n.language),
  };
};

export default useLanguageSelector;
